(() => {
  'use strict';
  if (window.ArdanShieldFinder) return;
  const allowed = {
    opening: ['door','cabinet','key_safe','shared','hospitality','unknown'],
    use: ['home','rental','hmo','hospitality','commercial'],
    construction: ['upvc','composite','aluminium','timber','glass','unknown'],
    arrangement: ['lift','mortice','euro','tubular','night','unknown'],
    access: ['pin','app','rfid','key','fingerprint','facial'], remote: ['local','required','unsure']
  };
  const storageKey = 'ardanshield-finder-review-v1';
  function steps(a) {
    if (!a.opening || a.opening === 'unknown') return ['opening'];
    if (['shared','hospitality'].includes(a.opening)) return ['opening','access','remote'];
    const list = ['opening','use'];
    if (a.opening === 'door' && !['hospitality','commercial','hmo'].includes(a.use)) {
      list.push('construction');
      if (a.construction && a.construction !== 'unknown') list.push('arrangement');
    }
    return list.concat('access','remote');
  }
  function plan(a) {
    if (!allowed.opening.includes(a.opening)) return {route:'review', tags:[]};
    if (['shared','hospitality'].includes(a.opening) || ['hospitality','commercial'].includes(a.use)) return {route:'project',tags:[]};
    if (a.opening === 'unknown' || a.use === 'hmo') return {route:'review',tags:[]};
    if (!allowed.use.includes(a.use) || !Array.isArray(a.access) || !a.access.length || a.access.some(x=>!allowed.access.includes(x)) || !allowed.remote.includes(a.remote)) return {route:'review',tags:[]};
    const tags = ['asf-enabled','asf-opening-'+a.opening,'asf-use-'+a.use];
    if (a.opening === 'door') {
      if (!allowed.construction.includes(a.construction) || a.construction === 'unknown' || !allowed.arrangement.includes(a.arrangement) || a.arrangement === 'unknown') return {route:'review',tags:[]};
      tags.push('asf-fit-'+a.construction+'-'+a.arrangement);
    }
    a.access.forEach(x=>tags.push('asf-access-'+x));
    return {route:'products',tags};
  }
  function matches(product,tags) {
    return tags.length > 0 && Array.isArray(product.tags) && tags.every(tag=>product.tags.includes(tag));
  }
  // Shared-entrance products are components for project review, never fit recommendations.
  function projectTags(a) {
    if (a.opening !== 'shared' || !Array.isArray(a.access) || !a.access.length || a.access.some(x=>!allowed.access.includes(x)) || !allowed.remote.includes(a.remote)) return [];
    const tags = ['asf-enabled','asf-opening-shared',...a.access.map(x=>'asf-access-'+x)];
    return tags;
  }
  window.ArdanShieldFinder = Object.freeze({plan,steps,matches,projectTags});
  function init(root) {
    if (root.dataset.finderReady) return;
    const copy = JSON.parse(root.querySelector('[data-finder-copy]').textContent);
    const form = root.querySelector('[data-finder-form]');
    const result = root.querySelector('[data-finder-result]');
    const next = root.querySelector('[data-finder-next]');
    const back = root.querySelector('[data-finder-back]');
    const more = root.querySelector('[data-finder-more]');
    const status = root.querySelector('[data-finder-status]');
    const list = root.querySelector('[data-finder-products]');
    const heading = root.querySelector('[data-finder-result-heading]');
    const description = root.querySelector('[data-finder-result-text]');
    const review = root.querySelector('[data-finder-review]');
    let current = 0, generation = 0, controller, nextPage, selectedPlan, started = false;
    const seen = new Set();
    root.dataset.finderReady = 'true';
    function answers() {
      const data = new FormData(form), a = {};
      for (const key of Object.keys(allowed)) a[key] = key === 'access' ? data.getAll(key) : data.get(key);
      return a;
    }
    function display(focus) {
      const active = steps(answers());
      current = Math.min(current, active.length - 1);
      root.querySelectorAll('[data-finder-step]').forEach(el=>{
        el.hidden = el.dataset.finderStep !== active[current];
        el.disabled = !active.includes(el.dataset.finderStep);
      });
      status.textContent = copy.step.replace('{current}',current+1).replace('{total}',active.length);
      root.querySelector('[data-finder-progress]').style.width = (answers().opening ? (current+1)/active.length*100 : 0)+'%';
      back.hidden = current === 0;
      updateRangeLinks();
      next.textContent = answers().opening && current === active.length-1 ? copy.show_results : copy.continue;
      if (focus) form.querySelector('fieldset:not([hidden]) legend').focus({preventScroll:true});
    }
    function updateRangeLinks() {
      const selected = answers().access;
      root.querySelectorAll('[data-finder-range]').forEach(el=>{
        el.hidden = !selected.includes(el.dataset.finderRange);
      });
    }
    function summarise() {
      const dl = root.querySelector('[data-finder-answers]'); dl.replaceChildren();
      const lines = [];
      for (const step of steps(answers())) {
        const fieldset = root.querySelector('[data-finder-step="'+step+'"]');
        const label = fieldset.querySelector('legend').textContent;
        const value = [...fieldset.querySelectorAll('input:checked')].map(input=>input.nextElementSibling.textContent).join(', ');
        const dt = document.createElement('dt'), dd = document.createElement('dd');
        dt.textContent = label; dd.textContent = value; dl.append(dt,dd);
        lines.push(label+' '+value);
      }
      try { sessionStorage.setItem(storageKey,JSON.stringify({created:Date.now(),text:copy.summary+'\n'+lines.join('\n'),notice:copy.transfer_notice,button:copy.transfer_button})); } catch {}
    }
    function link(url) {
      const u = new URL(url,location.origin);
      if (u.origin !== location.origin || !/^https?:$/.test(u.protocol)) throw new Error('Invalid product destination');
      return u.href;
    }
    async function load(page) {
      const thisRun = generation;
      controller?.abort(); controller = new AbortController();
      const activeController = controller;
      const timeout = setTimeout(()=>activeController.abort(),12000);
      more.disabled = true; result.setAttribute('aria-busy','true'); status.textContent = copy.loading;
      try {
        const url = new URL(root.dataset.searchUrl,location.origin);
        url.searchParams.set('q',selectedPlan.tags.map(t=>'tag:"'+t+'"').join(' AND '));
        url.searchParams.set('type','product'); url.searchParams.set('view','as-finder');
        url.searchParams.set('options[prefix]','none'); url.searchParams.set('page',page);
        const response = await fetch(url,{signal:activeController.signal,credentials:'same-origin'});
        if (!response.ok) throw new Error('Search failed');
        const data = JSON.parse(await response.text());
        if (data.version !== 1 || data.page !== page || !Array.isArray(data.products)) throw new Error('Invalid response');
        if (thisRun !== generation) return;
        data.products.filter(p=>matches(p,selectedPlan.tags)).forEach(p=>{
          if (seen.has(p.id)) return;
          const destination = link(p.url);
          const card = document.createElement('li'), a = document.createElement('a'), title = document.createElement('h3'), price = document.createElement('p'), cta = document.createElement('span');
          a.href = destination;
          if (p.image) { const image = document.createElement('img'); image.src = p.image; image.alt = ''; image.width = 300; image.height = 300; image.loading = 'lazy'; a.append(image); }
          title.textContent = p.title; price.textContent = (p.priceVaries ? copy.from+' ' : '')+p.price;
          cta.textContent = p.available ? copy.view_product : copy.unavailable;
          a.append(title,price,cta); card.append(a); list.append(card); seen.add(p.id);
        });
        nextPage = Number.isInteger(data.nextPage) && data.nextPage === page+1 ? data.nextPage : null;
        more.hidden = !nextPage;
        description.textContent = selectedPlan.route === 'project' ? (seen.size ? copy.controller_note : copy.controller_no_results) : seen.size ? copy.remote_note : copy.no_results;
        status.textContent = copy.loaded.replace('{count}',seen.size);
      } catch (error) {
        if (thisRun !== generation) return;
        description.textContent = copy.error; status.textContent = copy.error;
        nextPage = page; more.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (thisRun === generation) { more.disabled = false; result.removeAttribute('aria-busy'); }
      }
    }
    function finish() {
      generation++; controller?.abort(); seen.clear(); list.replaceChildren(); more.hidden = true;
      selectedPlan = plan(answers()); summarise();
      if (selectedPlan.route === 'project') selectedPlan.tags = projectTags(answers());
      updateRangeLinks();
      form.hidden = true; result.hidden = false;
      root.querySelector('[data-finder-remote-warning]').hidden = answers().remote !== 'required';
      heading.textContent = copy[selectedPlan.route === 'products' ? 'results' : selectedPlan.route === 'project' ? 'project_result' : 'review_result'];
      description.textContent = selectedPlan.route === 'products' ? copy.loading : selectedPlan.route === 'project' ? (selectedPlan.tags.length ? copy.controller_note : copy.project_note) : copy.review_note;
      review.href = selectedPlan.route === 'project' ? root.dataset.projectUrl : root.dataset.reviewUrl;
      review.textContent = selectedPlan.route === 'project' ? copy.project : copy.review;
      status.textContent = heading.textContent; result.focus();
      document.dispatchEvent(new CustomEvent('ardanshield:finderCompleted'));
      if (selectedPlan.tags.length) load(1);
    }
    function advance() {
      const fieldset = form.querySelector('fieldset:not([hidden])');
      const valid = !!fieldset.querySelector('input:checked');
      fieldset.querySelector('[data-finder-error]').hidden = valid;
      if (!valid) { fieldset.querySelector('[data-finder-error]').focus(); return; }
      if (!started) { started = true; document.dispatchEvent(new CustomEvent('ardanshield:finderStarted')); }
      const active = steps(answers());
      if (current === active.length-1) finish();
      else { current++; display(true); }
    }
    next.addEventListener('click',advance);
    form.addEventListener('change',()=>display(false));
    form.addEventListener('submit',event=>{event.preventDefault();advance();});
    back.addEventListener('click',()=>{current--;display(true);});
    more.addEventListener('click',()=>load(nextPage));
    root.querySelector('[data-finder-restart]').addEventListener('click',()=>{
      generation++;controller?.abort();form.reset();current=0;started=false;form.hidden=false;result.hidden=true;result.removeAttribute('aria-busy');
      try { sessionStorage.removeItem(storageKey); } catch {}
      display(true);
    });
    form.hidden = false; display(false);
  }
  function boot(scope=document) { scope.querySelectorAll('[data-as-finder]').forEach(root=>{try {init(root);} catch {}}); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',()=>boot());
  else boot();
  document.addEventListener('shopify:section:load',event=>boot(event.target));
})();
